import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { sprintf } from 'sprintf-js';
import { makeStyles } from "../../../styles/index";
import { ChevronLeft, ChevronRight } from "../../../icons/index";
import { useTranslation } from "../../../hooks/useTranslation";
import { Button } from "../../Button/Button";
import { Divider } from "../../Divider/Divider";
import { Grid } from "../../Grid/Grid";
import { IconButton } from "../../IconButton/IconButton";
import { Popup } from "../../Popup/Popup";
import { PopupContainer } from "../../Popup/PopupContainer";
import { PopupContent } from "../../Popup/PopupContent";
import { TextField } from "../../TextField/TextField";
import { Typography } from "../../Typography/Typography";
var useStyles = makeStyles(function (theme) { return ({
    root: {
        display: 'flex',
    },
    date: {
        flex: '1 0 0',
        minWidth: 0,
    },
    monthsContainer: {
        maxWidth: 64 * 3,
    },
    monthButton: {
        padding: 0,
        margin: 0,
        minWidth: 64,
        '&$current': {
            color: theme.palette.primary.main,
        },
        '&$selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
        },
        '&$disabled': {
            color: theme.palette.light.text.disabled,
            pointerEvents: 'none',
        },
    },
    disabled: {},
    selected: {},
    current: {},
    actions: {
        padding: theme.spacing(1),
    },
    actionButton: {
        padding: 0,
        minWidth: 0,
        width: 64,
    },
}); }, { name: 'SnMonthPickerField' });
var MONTHS = Array.from(new Array(12)).map(function (v, i) { return ({
    id: "month-" + (i + 1),
    value: i + 1,
}); });
/**
 * 対象の年月が選択可能かどうかを返す。
 *
 * disableFutureがtrueで、現在の日付が対象の年月より古い時だけfalseとなる。
 *
 * @param targetDate 対象の年月
 * @param currentDate 現在のDateTime
 * @param disableFuture boolean
 * @param disablePast boolean
 */
var isMonthSelectable = function (targetDate, currentDate, disableFuture, disablePast) {
    return ((!disableFuture || currentDate.startOf('month') >= DateTime.local(targetDate.year, targetDate.month)) &&
        (!disablePast || currentDate.startOf('month') <= DateTime.local(targetDate.year, targetDate.month)));
};
/**
 * 年月選択フィールド。
 */
export var MonthPickerField = function (props) {
    var _a;
    var className = props.className, style = props.style, value = props.value, disableFuture = props.disableFuture, disablePast = props.disablePast, _b = props.clearable, clearable = _b === void 0 ? true : _b, onChange = props.onChange;
    var _c = useState(null), anchorRef = _c[0], setAnchorRef = _c[1];
    var _d = useState(false), isOpen = _d[0], setIsOpen = _d[1];
    var handleOpen = useCallback(function () { return setIsOpen(true); }, []);
    var handleClose = useCallback(function () { return setIsOpen(false); }, []);
    var _e = useState(2020), selectedYear = _e[0], setSelectedYear = _e[1];
    var currentDate = DateTime.local();
    useEffect(function () {
        var _a;
        if (isOpen) {
            setSelectedYear((_a = value === null || value === void 0 ? void 0 : value.year) !== null && _a !== void 0 ? _a : DateTime.local().year);
        }
    }, [isOpen, value]);
    var handleNextYear = useCallback(function () { return setSelectedYear(function (currentYear) { return Math.min(currentYear + 1, 9999); }); }, []);
    var handlePrevYear = useCallback(function () { return setSelectedYear(function (currentYear) { return Math.max(currentYear - 1, 0); }); }, []);
    var handleMonthChange = function (month) { return function () {
        if (!isMonthSelectable({ year: selectedYear, month: month }, currentDate, disableFuture, disablePast))
            return;
        var nextValue = (value !== null && value !== void 0 ? value : DateTime.local()).set({ year: selectedYear, month: month }).startOf('month');
        onChange === null || onChange === void 0 ? void 0 : onChange(nextValue);
        setIsOpen(false);
    }; };
    var handleClear = useCallback(function () {
        onChange === null || onChange === void 0 ? void 0 : onChange(null);
        setIsOpen(false);
    }, [onChange]);
    var t = useTranslation().t;
    var classes = useStyles(props);
    return (React.createElement(PopupContainer, { className: classNames(classes.root, className), style: style },
        React.createElement("div", { className: classes.date, ref: setAnchorRef },
            React.createElement(TextField, { readonly: true, dense: true, placeholder: t('○○○○年△△月'), value: (_a = value === null || value === void 0 ? void 0 : value.toFormat(t('yyyy年MM月'))) !== null && _a !== void 0 ? _a : '', onClick: handleOpen })),
        React.createElement(Popup, { open: isOpen, anchorEl: anchorRef, onClickAway: handleClose, portal: true, boundariesElement: "viewport" },
            React.createElement(PopupContent, null,
                React.createElement(Grid, { container: true, alignItems: "center", justify: "space-between" },
                    React.createElement(IconButton, { onClick: handlePrevYear },
                        React.createElement(ChevronLeft, null)),
                    React.createElement(Typography, null, sprintf(t('%04d年'), selectedYear)),
                    React.createElement(IconButton, { onClick: handleNextYear },
                        React.createElement(ChevronRight, null))),
                React.createElement(Divider, { thin: true }),
                React.createElement(Grid, { container: true, alignItems: "center", className: classes.monthsContainer }, MONTHS.map(function (month) {
                    var _a;
                    var disable = !isMonthSelectable({ year: selectedYear, month: month.value }, currentDate, disableFuture, disablePast);
                    var buttonClassName = classNames(classes.monthButton, (_a = {},
                        _a[classes.current] = (currentDate === null || currentDate === void 0 ? void 0 : currentDate.month) === month.value && (currentDate === null || currentDate === void 0 ? void 0 : currentDate.year) === selectedYear,
                        _a[classes.selected] = (value === null || value === void 0 ? void 0 : value.month) === month.value && (value === null || value === void 0 ? void 0 : value.year) === selectedYear,
                        _a[classes.disabled] = disable,
                        _a));
                    return (React.createElement(Button, { variant: "text", className: buttonClassName, onClick: handleMonthChange(month.value) }, sprintf(t('%d月'), month.value)));
                }))),
            clearable && (React.createElement(React.Fragment, null,
                React.createElement(Divider, { thin: true }),
                React.createElement(Grid, { container: true, className: classes.actions, alignItems: "center", justify: "space-between" },
                    React.createElement(Button, { variant: "text", className: classes.actionButton, onClick: handleClear }, "\u30AF\u30EA\u30A2"),
                    React.createElement(Button, { variant: "square", fill: true, className: classes.actionButton, onClick: handleClose }, "OK")))))));
};
